/**
 * Segnalazioni in linea (FBK26): modalità segnalazione di @brandart/feedback-kit attorno all'app autenticata.
 * Si accende solo se il backend dice di sì (`/feedback/config`): servizio configurato e utente interno. Mai per i
 * referenti dei fornitori. I dati di contatto dei fornitori sono marcati `data-sensitive` e arrivano oscurati; le tendine
 * antd che li mostrano stanno in un portal su body e si oscurano con `maskSelectors` (vedi components/Sensitive.tsx).
 * L'ambiente (test | prod) lo dice il backend (FEEDBACK_ENVIRONMENT), non l'hostname.
 */
import "@brandart/feedback-kit/styles.css";
import { EN_TEXTS, FeedbackButton, FeedbackProvider } from "@brandart/feedback-kit";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { api, apiUrl } from "../api/client";
import { SENSITIVE_MASK_SELECTORS } from "./Sensitive";

export function FeedbackShell({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const { i18n } = useTranslation();
  const cfg = useQuery({
    queryKey: ["feedback-config", me?.id],
    queryFn: () => api.get<{ enabled: boolean; environment: "test" | "prod" }>("/feedback/config"),
    enabled: !!me && me.role !== "supplier",
    staleTime: Infinity,
    retry: false,
  });
  const ver = useQuery({ queryKey: ["version"], queryFn: () => api.get<{ version: string; commit: string | null }>("/version"), staleTime: Infinity });
  const enabled = !!me && me.role !== "supplier" && cfg.data?.enabled === true && !!ver.data;
  return (
    <FeedbackProvider
      config={{
        app: "audit-plan",
        appVersion: ver.data?.version ?? "dev",
        environment: cfg.data?.environment ?? "test",
        buildSha: ver.data?.commit ?? undefined,
        endpoint: apiUrl("/feedback"),
        // sensitive-ok: identità di chi segnala per l'SDK, non si mostra
        user: me ? { name: me.name, email: me.email } : undefined,
        maskSelectors: SENSITIVE_MASK_SELECTORS,
        // testi del kit nella lingua dell'app (italiano di default)
        texts: i18n.language === "en" ? EN_TEXTS : undefined,
        enabled,
      }}
    >
      {children}
      {enabled && <FeedbackButton />}
    </FeedbackProvider>
  );
}
